import { motion, useMotionValue, useSpring } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Plus, Truck, ShieldCheck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatBRL, categoryLabel } from "@/lib/format";
import { Stars } from "@/components/Stars";

export default function ProductCard({ product, index = 0 }) {
  const navigate = useNavigate();
  const { addItem } = useCart();

  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 160, damping: 18 });
  const sry = useSpring(ry, { stiffness: 160, damping: 18 });

  const soldOut = product.stock <= 0;
  const lowStock = !soldOut && product.stock <= 5;

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 10);
    rx.set(-py * 10);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.07, ease: [0.22, 1, 0.36, 1] }}
      className="group"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid={`product-card-${product.id}`}
    >
      <motion.div
        style={{ rotateX: srx, rotateY: sry, transformPerspective: 900, transformStyle: "preserve-3d" }}
        onClick={() => navigate(`/produto/${product.id}`)}
        className="relative bg-surface border border-white/10 rounded-lg overflow-hidden cursor-pointer hover:border-cyan/40 transition-colors duration-300 h-full flex flex-col"
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-elevated">
          <img
            src={product.image_url}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent opacity-70" />
          {product.featured && !soldOut && (
            <span className="absolute top-3 left-3 text-[11px] uppercase tracking-[0.16em] bg-cyan text-ink font-semibold px-2.5 py-1 rounded-sm">
              Destaque
            </span>
          )}
          <span
            className="absolute bottom-3 left-3 flex items-center gap-1.5 text-[11px] bg-ink/80 border border-cyan/40 text-cyan px-2.5 py-1 rounded-sm"
            data-testid={`free-shipping-badge-${product.id}`}
          >
            <Truck size={11} />
            Frete grátis
          </span>
          {product.name.includes("(Lacrado)") && (
            <span
              className="absolute bottom-3 right-3 flex items-center gap-1.5 text-[11px] bg-ink/80 border border-emerald-400/40 text-emerald-300 px-2.5 py-1 rounded-sm"
              data-testid={`warranty-badge-${product.id}`}
            >
              <ShieldCheck size={11} />
              Garantia de 1 ano
            </span>
          )}
          {lowStock && (
            <span
              className="absolute top-3 right-3 text-[11px] bg-ink/80 border border-white/15 text-amber-300 px-2.5 py-1 rounded-sm"
              data-testid={`product-low-stock-${product.id}`}
            >
              Últimas {product.stock}
            </span>
          )}
          {soldOut && (
            <div className="absolute inset-0 bg-ink/70 flex items-center justify-center">
              <span className="text-sm uppercase tracking-[0.2em] text-white/80 border border-white/30 px-4 py-2">
                Esgotado
              </span>
            </div>
          )}
        </div>

        <div className="p-4 flex flex-col gap-1.5 flex-1">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-[0.18em] text-cyan">
              {categoryLabel(product.category)}
            </span>
            {product.brand && (
              <span className="text-[11px] text-mute">{product.brand}</span>
            )}
          </div>
          <h3 className="font-display font-medium text-lg leading-snug">
            {product.name}
          </h3>
          {product.rating_count > 0 && (
            <div className="flex items-center gap-1.5" data-testid={`product-rating-${product.id}`}>
              <Stars value={product.rating_avg} size={12} />
              <span className="text-[11px] text-mute">
                {Number(product.rating_avg).toFixed(1).replace(".", ",")} ({product.rating_count})
              </span>
            </div>
          )}
          <div className="mt-auto pt-3 flex items-center justify-between">
            <span
              className="font-display text-xl text-white"
              data-testid={`product-price-${product.id}`}
            >
              {formatBRL(product.price)}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!soldOut) addItem(product, 1);
              }}
              disabled={soldOut}
              aria-label={`Adicionar ${product.name} ao carrinho`}
              className="h-9 w-9 rounded-md border border-white/15 flex items-center justify-center text-white hover:bg-cyan hover:text-ink hover:border-cyan disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white transition-all duration-300 hover:-translate-y-0.5"
              data-testid={`product-add-button-${product.id}`}
            >
              <Plus size={17} />
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
